// HookFormTableInput.tsx
import { FC, ComponentType } from 'react';
import { useFieldArray, Control, FieldErrors } from 'react-hook-form';
import {
  IconButton,
  Table,
  TableBody,
  TableBodyProps,
  TableCell,
  TableRow,
  TableCellProps,
  TableProps,
  TableRowProps,
  TableHead,
  SxProps,
  Theme,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { IconX, IconPlus } from '@tabler/icons-react';

type TableInputFieldProps = {
  // TODO: Replace with specific type in UI-2732
  component: ComponentType<any>;
  name: string;
  props?: object;
  label?: string;
  tableCellProps?: TableCellProps;
};

type HookFormTableInputProps = {
  control: Control;
  name: string;
  fields: TableInputFieldProps[];
  errors: FieldErrors;
  componentProps?: {
    tableProps?: TableProps;
    tableBodyProps?: TableBodyProps;
    tableRowProps?: TableRowProps;
    tableCellProps?: TableCellProps;
  };
  disabled?: boolean;
  insertAtTop?: boolean;
};

export const HookFormTableInput: FC<HookFormTableInputProps> = ({
  control,
  name,
  fields,
  errors,
  componentProps: {
    tableProps = {},
    tableBodyProps = {},
    tableRowProps = {},
    tableCellProps = {},
  } = {},
  disabled = false,
  insertAtTop = false,
}) => {
  const theme = useTheme();
  const {
    fields: fieldArrayFields,
    append,
    prepend,
    remove,
  } = useFieldArray({ control, name });

  return (
    <Table {...tableProps}>
      <TableHead>
        <TableRow {...tableRowProps}>
          <TableCell
            {...tableCellProps}
            padding="checkbox"
            sx={{ border: 'none', m: 0, py: 0, ...tableCellProps?.sx }}
          >
            <IconButton
              aria-label="add"
              onClick={() => {
                const newRow = Object.fromEntries(
                  fields.map(({ name: fieldName }) => [fieldName, ''])
                );
                insertAtTop ? prepend(newRow) : append(newRow);
              }}
              disabled={disabled}
            >
              <IconPlus
                color={
                  disabled
                    ? theme.palette.grey[500]
                    : theme.palette.primary.main
                }
              />
            </IconButton>
          </TableCell>
          {fields.map(({ label, tableCellProps: fieldTableCellProps }, id) => {
            const { sx: baseSx, ...baseProps } = tableCellProps || {};
            const { sx: fieldSx, ...fieldProps } = fieldTableCellProps || {};
            const combinedSx: SxProps<Theme> = [
              { border: 'none', m: 0, py: 0 },
              ...(baseSx ? (Array.isArray(baseSx) ? baseSx : [baseSx]) : []),
              ...(fieldSx
                ? Array.isArray(fieldSx)
                  ? fieldSx
                  : [fieldSx]
                : []),
            ];
            return (
              <TableCell
                key={id}
                {...baseProps}
                {...fieldProps}
                sx={combinedSx}
              >
                {label}
              </TableCell>
            );
          })}
        </TableRow>
      </TableHead>
      <TableBody {...tableBodyProps}>
        {fieldArrayFields.map((item, index) => (
          <TableRow key={item.id} {...tableRowProps}>
            <TableCell
              {...tableCellProps}
              padding="checkbox"
              sx={{ border: 'none', m: 0, py: 0, ...tableCellProps?.sx }}
            >
              <IconButton
                aria-label="delete"
                onClick={() => remove(index)}
                disabled={disabled}
              >
                <IconX
                  color={
                    disabled
                      ? theme.palette.grey[500]
                      : theme.palette.error.main
                  }
                />
              </IconButton>
            </TableCell>
            {fields.map(
              (
                {
                  component: InputComponent,
                  name: subFieldName,
                  props,
                  tableCellProps: fieldTableCellProps,
                },
                id
              ) => {
                const { sx: baseSx, ...baseProps } = tableCellProps || {};
                const { sx: fieldSx, ...fieldProps } =
                  fieldTableCellProps || {};
                const combinedSx: SxProps<Theme> = [
                  { border: 'none', m: 0, py: 0 },
                  ...(baseSx
                    ? Array.isArray(baseSx)
                      ? baseSx
                      : [baseSx]
                    : []),
                  ...(fieldSx
                    ? Array.isArray(fieldSx)
                      ? fieldSx
                      : [fieldSx]
                    : []),
                ];
                return (
                  <TableCell
                    key={id}
                    {...baseProps}
                    {...fieldProps}
                    sx={combinedSx}
                  >
                    <InputComponent
                      name={`${name}.${index}.${subFieldName}`}
                      control={control}
                      errors={errors}
                      {...props}
                    />
                  </TableCell>
                );
              }
            )}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};
